<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatListContainer from '../../../components/ChatListContainer.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from '../../../components/widgets/conversation/ConversationSidebar.vue';
import ForwardMessageModal from '../../../components/widgets/conversation/ForwardMessageModal.vue';
import AiOrderFormModal from './components/AiOrderFormModal.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import { getUserPermissions, filterItemsByPermission } from 'dashboard/helper/permissionsHelper.js';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import Modal from 'dashboard/components/Modal.vue';
import SearchView from 'dashboard/modules/search/components/SearchView.vue';

export default {
  components: {
    ChatListContainer,
    ConversationBox,
    ConversationSidebar,
    CmdBarConversationSnooze,
    ForwardMessageModal,
    AiOrderFormModal,
    Modal,
    SearchView,
  },
  beforeRouteLeave(to, from, next) {
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next();
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
      showForwardModal: false,
      forwardMessageData: null,
      activeAssigneeTab: wootConstants.ASSIGNEE_TYPE.ALL,
      showAiOrderFormModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      conversationStats: 'conversationStats/getStats',
      appliedFilters: 'getAppliedConversationFiltersV2',
    }),
    showConversationList() {
      // Always show conversation list unless explicitly hidden by layout toggle
      return !this.isOnExpandedLayout;
    },
    showMessageView() {
      // Always show message view if we have a conversation ID
      // Or if layout is not expanded (condensed mode)
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isContactPanelOpen() {
      if (this.currentChat.id) {
        const { is_contact_sidebar_open: isContactSidebarOpen } =
          this.uiSettings;
        return isContactSidebarOpen;
      }
      return false;
    },
    showPopOverSearch() {
      return true;
    },

    hasAppliedFilters() {
      return this.appliedFilters?.length > 0;
    },

    hasActiveFolders() {
      return Boolean(this.foldersId);
    },

    hasAppliedFiltersOrActiveFolders() {
      return this.hasAppliedFilters || this.hasActiveFolders;
    },

    userPermissions() {
      return getUserPermissions(this.currentUser, this.accountId);
    },

    assigneeTabItems() {
      return filterItemsByPermission(
        ASSIGNEE_TYPE_TAB_PERMISSIONS,
        this.userPermissions,
        item => item.permissions
      ).map(({ key, count: countKey }) => ({
        key,
        name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
        count: this.conversationStats[countKey] || 0,
      }));
    },

    // Mobile pane selection: only one visible on small screens
    activeMobilePane() {
      // Priority: contact panel (if open) -> conversation (if has conversationId) -> chat list
      if (this.isContactPanelOpen && this.currentChat.id) return 'contact';
      if (this.conversationId) return 'conversation';
      return 'list';
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
    inboxId() {
      // Ensure list is visible when switching (or re-selecting) inbox without a conversation selected
      if (!this.conversationId && this.isOnExpandedLayout) {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: LAYOUT_TYPES.CONDENSED,
        });
      }
    },
    // Watch for route changes to auto-expand when returning to conversations
    '$route'(to) {
      // If navigating to conversation route and layout is collapsed, auto-expand
      if (to.name && to.name.includes('conversation') && this.isOnExpandedLayout) {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: LAYOUT_TYPES.CONDENSED,
        });
      }
      // If navigating without a conversation ID and layout is expanded, auto-expand to show list
      if (!to.params.conversationId && this.isOnExpandedLayout) {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: LAYOUT_TYPES.CONDENSED,
        });
      }
    },
  },

  created() {
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
      if (this.isOnExpandedLayout) {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: LAYOUT_TYPES.CONDENSED,
        });
      }
    } else {
      // Ensure conversation is loaded if we have an ID
      this.fetchConversationIfUnavailable();
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    emitter.on('OPEN_FORWARD_MESSAGE_MODAL', this.openForwardModal);
    emitter.on('OPEN_AI_ORDER_FORM_MODAL', this.openAiOrderFormModal);
  },

  beforeUnmount() {
    emitter.off('OPEN_FORWARD_MESSAGE_MODAL', this.openForwardModal);
    emitter.off('OPEN_AI_ORDER_FORM_MODAL', this.openAiOrderFormModal);
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
      // If no conversation is selected, always keep the list visible
      if (!this.conversationId && this.isOnExpandedLayout) {
        const { LAYOUT_TYPES } = wootConstants;
        this.updateUISettings({
          conversation_display_type: LAYOUT_TYPES.CONDENSED,
        });
      }
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      
      
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },   
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        if (!selectedConversation) {
          return;
        }
        const { messageId } = this.$route.query;
       
        this.showSearchModal = false;

        if (selectedConversation.id === this.currentChat.id) {
          if (messageId) {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          }
          return;
        }

        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    }, 
    onToggleContactPanel() {
      this.updateUISettings({
        is_contact_sidebar_open: !this.isContactPanelOpen,
      });
    },
    onSearch() {
      this.showSearchModal = true;
    },   
    closeSearch() {
      this.showSearchModal = false;
    },
    openForwardModal(data) {
      this.forwardMessageData = data;
      this.showForwardModal = true;
    },
    closeForwardModal() {
      this.showForwardModal = false;
      this.forwardMessageData = null;
    },
    openAiOrderFormModal() {
      this.showAiOrderFormModal = true;
    },
    closeAiOrderFormModal() {
      this.showAiOrderFormModal = false;
    },
    async onForwardMessage(forwardData) {
      try {
        await this.$store.dispatch('forwardMessage', {
          conversationId: forwardData.conversationId,
          messageId: forwardData.messageId,
          targetConversationId: forwardData.targetConversationId,
        });
        this.closeForwardModal();
        this.$store.dispatch('alerts/show', {
          message: this.$t('CONVERSATION.FORWARD_MESSAGE.SUCCESS'),
          type: 'success',
        });
      } catch (error) {
        this.$store.dispatch('alerts/show', {
          message: this.$t('CONVERSATION.FORWARD_MESSAGE.ERROR'),
          type: 'error',
        });
      }
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        this.activeAssigneeTab = selectedTab;
        emitter.emit('clearSearchInput');
        emitter.emit('ASSIGNEE_TAB_CHANGED', selectedTab);
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full flex bg-slate-50 dark:bg-slate-900">
    <!-- Main Content Area -->
    <div class="flex min-h-0 w-full flex-1 p-0 xl:p-3 gap-3">
      <!-- Chat List -->
      <div
        class="transition-all duration-300 xl:flex-shrink-0 flex-1 xl:flex-none min-w-0"
        :class="[
          // mobile: only show when activeMobilePane is 'list'
          activeMobilePane === 'list' ? 'block w-full' : 'hidden',
          // md+: keep previous show/hide and sizing behavior
          showConversationList ? 'xl:block xl:w-96' : 'xl:block xl:w-0 xl:overflow-hidden'
        ]"
      >
        <div
          class="h-full transition-opacity duration-300"
          :class="showConversationList ? 'opacity-100' : 'xl:opacity-0 opacity-100'"
        >
          <ChatListContainer
            :show-conversation-list="true"
            :conversation-inbox="inboxId"
            :label="label"
            :team-id="teamId"
            :conversation-type="conversationType"
            :folders-id="foldersId"
            :is-on-expanded-layout="isOnExpandedLayout"
            :active-assignee-tab="activeAssigneeTab"
            :assignee-tab-items="assigneeTabItems"
            :has-applied-filters-or-active-folders="hasAppliedFiltersOrActiveFolders"
            :show-pop-over-search="showPopOverSearch"
            @conversation-load="onConversationLoad"
            @chat-tab-change="updateAssigneeTab"
            @toggle-conversation-layout="toggleConversationLayout"
            @open-search="onSearch"
          />
        </div>
      </div>
      
      <!-- Main Conversation Area -->
      <div
        class="min-w-0 gap-3 flex-1"
        :class="[
          // mobile: hide whole area when viewing list; else show
          activeMobilePane === 'list' ? 'hidden xl:flex' : 'flex'
        ]"
      >
        <!-- Conversation Box -->
        <div
          class="transition-all duration-300"
          :class="[
            // mobile: only show when viewing conversation
            activeMobilePane === 'conversation' ? 'block w-full' : 'hidden',
            // md+: always visible and takes remaining width
            'xl:block xl:flex-1 xl:min-w-0'
          ]"
        >
          <div class="h-full bg-white bg-opacity-90 dark:bg-slate-800 dark:bg-opacity-90 rounded-2xl shadow-xl backdrop-blur-md overflow-hidden hover:shadow-2xl transition-all duration-300">
            <ConversationBox
              class="h-full"
              :inbox-id="inboxId"
              :is-contact-panel-open="isContactPanelOpen"
              :is-on-expanded-layout="isOnExpandedLayout"
              :toggle-conversation-layout="toggleConversationLayout"
              @contact-panel-toggle="onToggleContactPanel"
            />
          </div>
        </div>
        
        <!-- Contact Panel -->
        <div
          class="transition-all duration-300 ease-in-out xl:flex-shrink-0 flex-1 xl:flex-none min-w-0"
          :class="[
            // mobile: only show when viewing contact panel
            activeMobilePane === 'contact' ? 'block w-full' : 'hidden',
            // md+: open/close behavior with width transitions
            isContactPanelOpen ? 'xl:block xl:w-80 xl:opacity-100' : 'xl:block xl:w-0 xl:opacity-0 xl:overflow-hidden'
          ]"
        >
          <div
            v-if="isContactPanelOpen && currentChat.id"
            class="h-full bg-white bg-opacity-90 dark:bg-slate-800 dark:bg-opacity-90 rounded-2xl shadow-xl backdrop-blur-md overflow-hidden hover:shadow-2xl transition-all duration-300"
          >
            <ConversationSidebar
              class="h-full"
              :current-chat="currentChat"
              @toggle-contact-panel="onToggleContactPanel"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modals -->
    <CmdBarConversationSnooze />
    <ForwardMessageModal
      v-if="showForwardModal"
      :show="showForwardModal"
      :message-data="forwardMessageData"
      @close="closeForwardModal"
      @forward="onForwardMessage"
    />
    <AiOrderFormModal
      v-if="showAiOrderFormModal"
      :show="showAiOrderFormModal"
      :conversation-id="conversationId"
      @close="closeAiOrderFormModal"
    />
    <Modal
      v-model:show="showSearchModal"
      :on-close="closeSearch"
      :fullWidth="true"
      size="medium"
    >
      <SearchView :embed="true" />
    </Modal>
  </div>
</template>
